import React, { Fragment, useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import ProtectedHeader from '../../layout/ProtectedHeader';
import MetaData from '../../layout/MetaData';
import Loader from '../../layout/Loader';
import { FormattedMessage } from 'react-intl';
import { useAlert } from 'react-alert';
import { useDispatch } from 'react-redux';
import { IMAGES } from '../../../constants';
import { socket } from '../../../utils/socket';
import { updateChatDisappearAction } from '../../../actions/chatAction';

const DURATION_24H = 86400;
const DURATION_7D = 604800;

const ChatPrivacySettings = () => {
    const alert = useAlert();
    const dispatch = useDispatch();
    const [selectedSeconds, setSelectedSeconds] = useState(0);
    const [savedSeconds, setSavedSeconds] = useState(0);
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!socket) return;

        const onSettings = (res) => {
            if (res?.status && res?.data) {
                const value = Number(res.data.bulk_seconds) || 0;
                setSelectedSeconds(value);
                setSavedSeconds(value);
            }
            setLoaded(true);
        };

        const onBulkSaved = (res) => {
            setSaving(false);
            if (res?.status) {
                const duration = Number(res.data?.duration_seconds) || 0;
                setSelectedSeconds(duration);
                setSavedSeconds(duration);
                (res.data?.group_ids || []).forEach((groupId) => {
                    dispatch(updateChatDisappearAction(groupId, duration));
                });
                alert.success(res.msg || 'Saved');
            } else {
                alert.error(res?.msg || 'Could not save setting');
            }
        };

        socket.emit('get_disappear_settings', {});
        socket.off('disappear_settings_response').on('disappear_settings_response', onSettings);
        socket.off('set_disappear_bulk_response').on('set_disappear_bulk_response', onBulkSaved);

        return () => {
            socket.off('disappear_settings_response', onSettings);
            socket.off('set_disappear_bulk_response', onBulkSaved);
        };
    }, [alert, dispatch]);

    const handleSubmit = (event) => {
        event.preventDefault();
        if (saving) return;
        setSaving(true);
        socket.emit('set_disappear_bulk', { duration_seconds: Number(selectedSeconds) || 0 });
    };

    const handleCancel = () => {
        setSelectedSeconds(savedSeconds);
    };

    return (
        <Fragment>
            {(saving || !loaded) && <Loader />}
            <MetaData title="Chat privacy" />
            <ProtectedHeader />
            <ul className="breadcrub">
                <li>
                    <FormattedMessage
                        id="myaccount.myaccountname"
                        defaultMessage="myaccount"
                    />
                </li>
                <li><img src={IMAGES.ARROW_SVG} alt="" /></li>
                <li>
                    <FormattedMessage
                        id="myaccount.chat_privacy"
                        defaultMessage="Chat privacy"
                    />
                </li>
            </ul>

            <section className="container-fluid py-4 conPart">
                <div className="row">
                    <Sidebar />
                    <div className="col-md-9 pt-4">
                        <div className="titles mb-4 nbrd">
                            <div className="row">
                                <div className="col-md-12">
                                    <h2 className="m-0">
                                        <FormattedMessage
                                            id="myaccount.chat_privacy"
                                            defaultMessage="Chat privacy"
                                        />
                                    </h2>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-11 col-lg-6">
                                    <div className="form-group">
                                        <label>
                                            <FormattedMessage
                                                id="chat.disappear.bulk_title"
                                                defaultMessage="Auto-disappearing messages"
                                            />
                                        </label>
                                        <p className="chat-privacy-help mb-3">
                                            <FormattedMessage
                                                id="chat.disappear.bulk_help"
                                                defaultMessage="Turn on for all chats at once. Messages are removed after the selected duration. You can turn this off for any individual chat from the chat header."
                                            />
                                        </p>

                                        <label className="cust_radio">
                                            Off
                                            <input
                                                type="radio"
                                                name="bulk_disappear"
                                                checked={Number(selectedSeconds) === 0}
                                                disabled={saving || !loaded}
                                                onChange={() => setSelectedSeconds(0)}
                                            />
                                            <span className="checkmark"></span>
                                        </label>

                                        <label className="cust_radio">
                                            24 hours
                                            <input
                                                type="radio"
                                                name="bulk_disappear"
                                                checked={Number(selectedSeconds) === DURATION_24H}
                                                disabled={saving || !loaded}
                                                onChange={() => setSelectedSeconds(DURATION_24H)}
                                            />
                                            <span className="checkmark"></span>
                                        </label>

                                        <label className="cust_radio">
                                            7 days
                                            <input
                                                type="radio"
                                                name="bulk_disappear"
                                                checked={Number(selectedSeconds) === DURATION_7D}
                                                disabled={saving || !loaded}
                                                onChange={() => setSelectedSeconds(DURATION_7D)}
                                            />
                                            <span className="checkmark"></span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="row actions my-5">
                                <div className="col-md-11 col-lg-6">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="btns outline b-op"
                                        disabled={saving || !loaded}
                                    >
                                        <FormattedMessage id="addsingleproduct.cancel" defaultMessage="Cancel" />
                                    </button>
                                    <button
                                        type="submit"
                                        className="btns mr-3 sndBtn"
                                        disabled={saving || !loaded || Number(selectedSeconds) === Number(savedSeconds)}
                                    >
                                        <FormattedMessage id="Save" defaultMessage="Save" />
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </section>
        </Fragment>
    );
};

export default ChatPrivacySettings;
